import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProjectHomeSummary, type ProjectHomeSummaryDto } from '../../api/project-home';
import { listMyTasks, listProjects, type MyTaskDto, type TaskProjectDto } from '../../api/tasks';
import { listTeams, type TeamDto } from '../../api/project-teams';
// ⛔ 2026-10-12 — ProjectStatsCards 를 걷었다(통계 뒷정리 · 운영에서 안 그려지던 부품).
import { MyTaskProgressList } from './MyTaskProgressList';
import {
  ProjectHomeNav,
  PROJECT_NAV_DEFAULT_EXPANDED,
  type ProjectNavKey,
  type ProjectNavExpandKey,
} from './ProjectHomeNav';
import { projectNavTarget, projectSearchTarget, projectSpaceTarget } from './project-nav-targets';
// ⛔ 2026-10-12 — WeeklyRateWidget 을 걷었다(남은 둘은 그대로 쓴다).
import { TeamsWidget, ProjectsWidget } from './ProjectHomeWidgets';
import { ProjectHomeSpaces } from './ProjectHomeSpaces';
import { ProjectSearchBox } from './ProjectSearchBox';
/*
 * ⭐ 2026-10-10 (배선 판) — ★ AW-001 갈래가 ⭐ 쓰는 셋.
 *   ⇒ ⚠ 셋 다 ★ 「if (PROJECT_HOME_AW001)」 갈래 «안»에서만 그려진다
 *      ⇒ ⇒ ★ 운영은 그 깃발이 ⭐ 0 이라(.env.production:10) ⇒ ⇒ 번들에는 들어가지만 ★ 안 그려진다.
 */
import { ProjectHomeProjects } from './ProjectHomeProjects';
// 2026-08-19 — 프로젝트 홈의 [＋ 새 스페이스 만들기] 는 시안대로 '스페이스 생성'(색+이름)만 묻는다.
// 멤버 초대까지 있는 큰 모달(TeamCreateInviteModal, AW-003)은 팀 화면의 [새 팀] 이 계속 쓴다.
import { SpaceCreateModal } from './SpaceCreateModal';
// 2026-08-21 — 카드 격자의 [＋ 새 프로젝트] 도 그 자리에서 만들 수 있게(스페이스와 같은 흐름).
import { ProjectCreateSimpleModal } from './ProjectCreateSimpleModal';
import { PROJECT_HOME_3C, PROJECT_HOME_AW001 } from '../../lib/task-shell';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './ProjectPage.css';

/**
 * ProjectPage (project-menu v2 — 카드 드릴다운) — /project 메인 = 읽기 전용 요약 대시보드.
 *
 * 설계: docs/design/architecture-project-menu.md §3.2 (v2)
 *   ① 통계 카드 3종 — 전부 클릭 가능 → 상세 화면 드릴다운
 *      (/project/teams · /project/projects · /project/my-tasks)
 *      ⛔ 2026-10-12 — 종전에는 넷이고 끝이 `/project/stats` 였다. 사용자 결정
 *        (「/project/stats 삭제해라 화면도 필요없다」)으로 그 카드와 화면을 걷었다.
 *   ② 내가 하는 일 (전폭) — 행 클릭 → /task?project=
 *   ✕ v1 의 팀 패널·프로젝트 목록·헤더 생성 버튼은 상세 화면으로 이전 (adr-20260610-0409).
 */
export function ProjectPage() {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const navigate = useNavigate();

  // ① 대시보드 집계
  const [summary, setSummary] = useState<ProjectHomeSummaryDto | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  // ② 내 할일
  const [myTasks, setMyTasks] = useState<MyTaskDto[]>([]);
  const [myTasksLoading, setMyTasksLoading] = useState(true);
  const [myTasksError, setMyTasksError] = useState<string | null>(null);
  // 3c — 좌측 2차 네비(내 팀 전개) + 위젯용 목록. 플래그 OFF 면 조회하지 않는다.
  const [teams, setTeams] = useState<TeamDto[]>([]);
  const [projects, setProjects] = useState<TaskProjectDto[]>([]);
  // 즐겨찾기도 펼침 대상이라 ProjectNavExpandKey — 라우팅에는 여전히 ProjectNavKey 만 쓴다.
  // 「하위 메뉴 기본 오픈」 — 값은 ProjectHomeNav 한 곳에서 온다(홈 밖 셸도 같은 값을 쓴다).
  /*
   * ⭐ 2026-10-10 (배선 판) — ★ `?view=projects` 로 올 때만 ⇒ ⭐ 「프로젝트」를 펼친 채 시작한다.
   *   ⇒ ⚠ ⛔ 기본값은 ★ 그대로 `PROJECT_NAV_DEFAULT_EXPANDED` 다 — ⇒ ⭐ 파라미터가 없으면 ★ 무변경
   *      ⇒ ⇒ ★ 그래서 ⭐ else 갈래(3c · 운영)는 ★ 한 글자도 안 달라진다.
   *   ⇒ ⇒ ⭐ 정본은 ★ 기본값 자체를 `teams` 로 적는데 ⇒ ⛔ 우리 기본값은 ★ 그 상수가 쥔다
   *      ⇒ ⇒ 그 상수는 ★ 홈 «밖» 셸(ProjectNavShell)도 쓰므로 ⇒ ⭐ 여기서 바꾸면 ★ 거기까지 번진다.
   */
  const [expandedNav, setExpandedNav] = useState<ProjectNavExpandKey | null>(
    () => (new URLSearchParams(window.location.search).get('view') === 'projects'
      ? 'projects' : PROJECT_NAV_DEFAULT_EXPANDED),
  );
  const [navCollapsed, setNavCollapsed] = useState(false);
  /*
   * ⭐ 2026-10-10 (배선 판) — ★ AW-001 갈래가 ⭐ 쓰는 상태 ★ 넷.
   *
   * ```
   * homeView       ⇒ 컨텐츠를 ★ «내 스페이스 ↔ 내 프로젝트»로 바꾼다 (정본 ?view= 를 읽는다)
   * creatingSpace  ⇒ [＋ 새 스페이스 만들기] ⇒ ★ SpaceCreateModal
   * creatingProject⇒ [＋ 새 프로젝트]        ⇒ ★ ProjectCreateSimpleModal
   * listsLoading   ⇒ 목록이 아직 안 왔나 ⇒ ★ 스켈레톤 카드를 세울지 (AW-005 ⑦)
   * ```
   * ⇒ ⚠ 넷 다 ★ AW-001 갈래 «안»에서만 읽힌다 ⇒ ⇒ ⭐ else 갈래(3c)는 ★ 이 값을 안 본다.
   */
  const [homeView, setHomeView] = useState<'spaces' | 'projects'>(
    /*
     * ⭐ 2026-09-16 (정본) — 다른 화면의 좌측 네비에서 [내 프로젝트] 를 누르면
     *   `/project?view=projects` 로 온다. ⇒ ★ 그때만 ⭐ 격자를 편 채로 시작한다.
     *   ⇒ ⇒ 파라미터가 없으면 ★ 종전대로 ⭐ «스페이스»다(⇒ 기존 진입 ★ 무영향).
     */
    () => (new URLSearchParams(window.location.search).get('view') === 'projects' ? 'projects' : 'spaces'),
  );
  const [creatingSpace, setCreatingSpace] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  // AW-005 — 팀·프로젝트 목록이 아직 오지 않았는지. 스켈레톤 카드를 세울지 판단한다.
  const [listsLoading, setListsLoading] = useState(PROJECT_HOME_3C);

  // 3c 통합 검색창이 적는 글자 (2026-10-07) — ⚠ 여기 머무를 뿐이고 조회는 «결과 화면»이 한다.
  //   ⇒ 그래서 적는 동안 서버를 안 부른다. 「찾기」를 누르면 /project/search?q= 로 «옮겨 간다».
  const [searchDraft, setSearchDraft] = useState('');

  const refreshSummary = useCallback(async () => {
    try {
      setSummary(await getProjectHomeSummary());
    } catch {
      /* 통계 실패는 카드 '—' 표시로 graceful (치명 아님). */
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const refreshMyTasks = useCallback(async () => {
    setMyTasksError(null);
    try {
      /*
       * AW-005 (2026-08-25) — limit 20 → 100. (정본 이관 판 5 · 2026-10-13 — 정본 주석 그대로)
       * 이 목록은 화면에 그대로 뿌려지기만 하는 게 아니라 홈의 파생 지표(지연 칩·위험 수·
       * 검색 미리 거르기)의 원천이기도 했다. 20건에서 센 숫자와 서버 전체 집계가 같은 줄에
       * 서면서 서로 어긋났다. 지금은 지연·위험 모두 서버 집계를 쓰지만, 구버전 서버 폴백
       * 경로가 남아 있어 모집단을 최대한 넓혀 둔다(`/tasks/me` 의 limit 상한이 100).
       */
      const res = await listMyTasks({ status: ['TODO', 'IN_PROGRESS', 'REVIEW'], limit: 100 });
      setMyTasks(Array.isArray(res.items) ? res.items : []);
    } catch (err) {
      setMyTasksError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err as Error).message ?? tProject('project.mytasks.err_fetch'));
    } finally {
      setMyTasksLoading(false);
    }
  }, []);

  /**
   * ⭐ **프로젝트 목록을 다시 읽는다** (2026-10-10 — ★ 휴지통 복원이 ⭐ 쓴다).
   *
   * ## ⛔ 왜 «꺼냈나» — ⇒ ★ 이름이 ⭐ 없었다
   *
   * 종전에는 아래 위젯 useEffect 안에 `void listProjects().then(setProjects)` 로 ★ «이름 없이»
   * 있었다. ⇒ ⛔ 그래서 ★ 밖에서 ⭐ «다시 읽어 달라»고 할 수가 없었다
   *   (⇒ `refreshSummary`·`refreshMyTasks` 는 이름이 있어 부를 수 있었는데 ★ 이것만 없었다).
   * ⇒ ✅ 그 둘과 ⭐ «같은 꼴»로 꺼냈다 — ⇒ ★ 셋이 한 자리에 모여 ⭐ 「무엇을 다시 읽나」가 한눈에 온다.
   *
   * ⚠ 깃발 판정(`PROJECT_HOME_3C`)은 ★ 여기 두지 ⭐ 않았다 — ⇒ 그것은 ★ «처음 읽을 때»의 조건이고
   *   ⇒ ⇒ 아래 useEffect 가 그대로 들고 있다. ⇒ ⭐ 복원처럼 «사람이 한 일» 뒤에는 ★ 늘 다시 읽는다.
   */
  const refreshProjects = useCallback(async () => {
    try {
      // ★ 「내 프로젝트」·「내 스페이스」 ⇒ 내가 «멤버»인 것만 (2026-10-12 사용자 「권고대로」)
      //   ⚠ 이 한 배열을 ProjectHomeProjects(내 프로젝트)와 ProjectHomeSpaces(내 스페이스 격자)에
      //     «함께» 넘긴다 ⇒ mine 을 켜면 둘이 같이 좁아진다. 이름이 둘 다 「내…」라 일관된다.
      //     ⇒ ⭐ 2026-10-12 사용자 ⇒ 「권고 대로」 ⇒ ⇒ 즉 이것이 «결정»이다(경고가 아니다).
      //     ⛔ 「한쪽만 좁히라」는 요청이 오면 ⇒ 이 배열을 «둘로 갈라야» 한다 — 그때 다시 잰다.
      setProjects(await listProjects({ mine: true }));
    } catch {
      /* 목록 실패는 위젯만 비고 화면은 살아 있게(기존 .catch 와 같은 뜻). */
      setProjects([]);
    }
  }, []);

  /**
   * ⭐ **휴지통에서 프로젝트를 되돌린 뒤** (2026-10-10) — ⇒ ★ 무엇을 다시 읽나.
   *
   *     ✅ refreshProjects  ⇒ ★ 되돌아온 프로젝트가 ⭐ «목록»에 다시 선다
   *     ✅ refreshSummary   ⇒ ★ ⭐ «통계 카드»의 수가 바뀐다(프로젝트 수가 늘었다)
   *     ⛔ refreshMyTasks   ⇒ ★ 넣지 ⭐ 않았다
   * ⛔ **왜 `refreshMyTasks` 를 안 넣었나 — ⇒ ⭐ 「안 쟀다」가 까닭이다.**
   *   「프로젝트를 지우면 그 업무가 `/tasks/me` 에서 ★ 빠지나」를 ⭐ 아무도 안 쟀다.
   *   ⇒ ⇒ ⛔ 빠지지 «않는다»면 그 조회는 ★ 아무것도 안 바꾸는 ⭐ 헛된 요청이다.
   *   ⇒ ⇒ ★ 그래서 ⭐ 「모른다」를 ★ 「넣어 두면 안전하다」로 ⛔ 바꾸지 않는다.
   *
   * ⏳ **만료 조건** — 「프로젝트를 지우면 그 업무가 `/tasks/me` 에서 빠지나」를 ★ 재는 사람이 생기면
   *   ⇒ ⇒ ⭐ 빠진다면 ★ 여기에 `refreshMyTasks` 를 ⭐ 더한다.
   */
  /*
   * ⭐ 2026-10-10 (배선 판) — ★ AW-001 갈래가 ⭐ 쓰는 «다시 읽기».
   *   ⇒ 정본 `refreshLists` 그대로 ⇒ ⇒ 팀·프로젝트 «목록»까지 다시 읽는다
   *      (⇒ 스페이스/프로젝트를 ★ 지우거나 되살린 뒤 ⭐ 격자가 바로 맞게)
   * ⇒ ⚠ 아래 `onProjectRestored` 는 ⛔ 그대로 둔다 — ⇒ ★ else 갈래(3c)가 ⭐ 그것을 쓴다.
   *   ⇒ ⇒ ★ 즉 ⭐ 「더하기」이고 ★ 바꾸기가 ⛔ 아니다.
   */
  const refreshLists = useCallback(() => {
    void listTeams().then(setTeams).catch(() => { /* 기존 목록 유지 */ });
    void refreshProjects();
    void refreshSummary();
    void refreshMyTasks();
  }, [refreshProjects, refreshSummary, refreshMyTasks]);

  const onProjectRestored = useCallback(() => {
    void refreshProjects();
    void refreshSummary();
  }, [refreshProjects, refreshSummary]);

  useEffect(() => {
    void refreshSummary();
    void refreshMyTasks();
  }, [refreshSummary, refreshMyTasks]);

  /**
   * AW-005 — 탭으로 돌아왔을 때 숫자를 다시 읽는다. (정본 이관 판 5 · 2026-10-13 — 정본 그대로)
   *
   * 종전에는 mount 때 한 번뿐이라, 하루 종일 켜 두면 히어로의 마감임박·지연이 어제 값인 채로
   * 남았다. 목록(teams·projects)까지 다시 읽지는 않는다 — 흔들리는 건 대개 기한 쪽 숫자고,
   * 격자를 통째로 갈아 끼우면 보고 있던 자리가 움직인다.
   */
  useEffect(() => {
    function onFocus() {
      if (document.visibilityState === 'hidden') return;
      void refreshSummary();
      void refreshMyTasks();
    }
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refreshSummary, refreshMyTasks]);

  // 3c 위젯 데이터 — 실패해도 위젯만 비고 화면은 살아 있게(대시보드 정책 동형).
  useEffect(() => {
    if (!PROJECT_HOME_3C) return;
    /*
     * ⭐ 2026-10-10 (배선 판) — ★ 부르는 것·손은 ⭐ 그대로이고 ⇒ ★ `allSettled` 로 ⭐ 감싸기만 했다.
     *   ⇒ 까닭 ⇒ ★ 「둘 다 끝났다」를 알아야 ⇒ ⭐ 스켈레톤을 걷을 수 있다(정본 주석 그대로:
     *      ⇒ ⇒ 「하나만 와도 스켈레톤을 걷으면 ★ 빈 격자가 잠깐 보인다」)
     * ⇒ ⚠ ⛔ else 갈래(3c)에 번지나 ⇒ ⇒ ★ 안 번진다:
     *   ⓐ 부르는 끝점 ★ 그대로(listTeams · refreshProjects) ⓑ 실패 손 ★ 그대로
     *   ⓒ 더한 것은 ★ `setListsLoading(false)` ⭐ 한 줄이고 ⇒ ★ 그 값을 읽는 곳은
     *      ⇒ ⇒ ⭐ AW-001 갈래의 `ProjectHomeSpaces listsLoading` ★ 하나뿐이다.
     */
    void Promise.allSettled([
      listTeams().then(setTeams).catch(() => setTeams([])),
      refreshProjects(),
    ]).then(() => setListsLoading(false));
  }, [refreshProjects]);

  const openProject = useCallback((projectId: string) => {
    // 3c → 3d. 팀 스코프까지 실어 보내면 열자마자 그 팀 맥락이 잡힌다(design-20260724 D-5).
    const team = projects.find((p) => p.id === projectId)?.teamId;
    navigate(team ? `/task?team=${team}&project=${projectId}` : `/task?project=${projectId}`);
  }, [navigate, projects]);

  // 목적지는 project-nav-targets 한 곳에서 온다 — 홈 밖 셸(ProjectNavShell)도 같은 것을 부른다.
  // (2026-07-25 에 '내 프로젝트' 목적지가 /project/projects → /task 로 바뀐 전례가 있어 모았다.)
  const openNav = useCallback((key: ProjectNavKey) => {
    /*
     * ⭐⭐ 2026-10-10 (배선 판) — ★ AW-001 갈래에서는 ⇒ ⭐ '내 프로젝트' 가 ★ «화면을 안 떠난다».
     *
     * ```
     * 정본 ProjectPage.tsx:162 ⇒ ★ 글자 그대로:
     *   if (PROJECT_HOME_AW001) { setHomeView(…); setExpandedNav(…); return; }
     * ⇒ 까닭(정본 주석) ⇒ 「2026-07-25 에는 3d(/task) 로 곧장 보냈는데,
     *    ⇒ ⇒ ★ 그 화면이 ⭐ 목록이 아니라 «칸반»이라 ⇒ 「목록을 눌렀더니 어떤 프로젝트 안」이 됐다」
     * ```
     *
     * ## ⛔⛔ 이 줄은 ★ «사용자 관문»에 닿는다 — ⇒ ⭐ 그래서 ★ 범위를 수로 적는다
     *
     * ```
     * ⇒ 관제 상시 제약 ⇒ 「★ 「내 프로젝트 → /task」와 /project/projects 입구는 ⭐ 사용자 관문(2026-10-08)」
     * ⇒ ⇒ ⭐ 그래서 ★ 이 가로채기는 ⇒ ⭐ `PROJECT_HOME_AW001` ★ «안»에서만 돈다
     *    ⇒ ⇒ ★ 운영은 그 깃발이 ⭐ 0 이다(.env.production:10 · 2026-08-18 사용자 지시)
     *       ⇒ ⇒ ⇒ ✅ 즉 ★ 운영의 「내 프로젝트」 입구는 ⭐ `/task` 그대로다 — ★ 한 글자도 안 바뀐다
     * ⇒ ⇒ ⚠ 바뀌는 것은 ★ 「그 깃발을 켠 뒤」의 ⭐ AW-001 홈 «안»에서다
     * ```
     * ⇒ ⚠ 주소는 ★ 여전히 ⭐ `projectNavTarget` 한 곳에서 온다 — ⇒ ★ 아래 줄을 ⛔ 안 건드렸다.
     */
    if (key === 'projects' && PROJECT_HOME_AW001) {
      setHomeView('projects');
      setExpandedNav('projects');
      return;
    }
    navigate(projectNavTarget(key));
  }, [navigate]);

  const openTeam = useCallback((teamId: string) => {
    // 2026-10-13 정본 맞춤 — 스페이스 «안»(프로젝트 목록)으로 간다(project-nav-targets 머리말).
    navigate(projectSpaceTarget(teamId));
  }, [navigate]);

  // 3c 프로젝트 홈 (레이아웃 SSOT all-frame.html 05.프로젝트 3c) — 좌측 2차 네비 + 위젯 4종.
  // 플래그 OFF 면 아래 기존 대시보드(숫자 카드 + 내가 하는 일)가 그대로 렌더된다(무회귀 롤백 경로).
  // AW-001(화면설계서 v2.2) 프로젝트메인 개선안 — 3c 셸(좌측 2차 네비)은 그대로 두고
  // 컨텐츠 영역만 새 구성(히어로 · 통합 검색 · 내 스페이스)으로 바꾼다.
  // 플래그 OFF 면 아래 3c 위젯 홈이 손대지 않은 그대로 렌더된다(무회귀 롤백 경로).
  if (PROJECT_HOME_AW001) {
    return (
      <div className={`pj-page pj-page--shell pj-home${navCollapsed ? ' pj-home--navoff' : ''}`}>
        <ProjectHomeNav
          expanded={expandedNav}
          onToggle={(k) => {
            // 펼침 항목(스페이스·즐겨찾기)을 누르면 컨텐츠도 스페이스 격자로 돌아온다 —
            // 목록만 펼쳐지고 오른쪽은 프로젝트인 어긋난 상태를 만들지 않으려는 것.
            setHomeView('spaces');
            setExpandedNav((prev) => (prev === k ? null : k));
          }}
          teams={teams}
          onOpenNav={openNav}
          onOpenTeam={openTeam}
          onCollapse={() => setNavCollapsed(true)}
          // 휴지통에서 되돌리면 오른쪽 격자·요약까지 즉시 다시 읽는다 (2026-09-07).
          onRestored={refreshLists}
        />

        <main className="pj-home-content scrollbar-thin">
          {navCollapsed && (
            <button
              type="button"
              className="pj-teams-side-new pj-home-navexpand"
              onClick={() => setNavCollapsed(false)}
              aria-label={tProject('project.home.nav_collapse')}
              title={tProject('project.home.nav_collapse')}
            >
              »
            </button>
          )}

          {homeView === 'projects' ? (
            <ProjectHomeProjects
              projects={projects}
              teams={teams}
              myTasks={myTasks}
              summary={summary}
              onOpenProject={openProject}
              onCreateProject={() => setCreatingProject(true)}
              onProjectDeleted={refreshLists}
            />
          ) : (
            <ProjectHomeSpaces
              summary={summary}
              summaryLoading={summaryLoading}
              teams={teams}
              projects={projects}
              myTasks={myTasks}
              onOpenTeam={openTeam}
              onOpenProject={openProject}
              onCreateSpace={() => setCreatingSpace(true)}
              listsLoading={listsLoading}
              onOpenStat={(f) => navigate(`/project/my-tasks?filter=${f}`)}
              onOpenPath={(to) => navigate(to)}
              onSpaceDeleted={refreshLists}
            />
          )}
        </main>

        {creatingProject && (
          <ProjectCreateSimpleModal
            teams={teams}
            initialTeamId={null}
            onClose={() => setCreatingProject(false)}
            onCreated={() => {
              setCreatingProject(false);
              // 목록을 다시 읽어 방금 만든 카드가 바로 보이게 한다.
              void refreshProjects();
            }}
          />
        )}

        {creatingSpace && (
          <SpaceCreateModal
            onClose={() => setCreatingSpace(false)}
            onCreated={(team) => {
              setCreatingSpace(false);
              // 만든 직후 그 스페이스 안으로 들어간다 — 시안의 "내 프로젝트 목록" 화면.
              navigate(projectSpaceTarget(team.id));
            }}
          />
        )}
      </div>
    );
  }

  if (PROJECT_HOME_3C) {
    return (
      <div className={`pj-page pj-page--shell pj-home${navCollapsed ? ' pj-home--navoff' : ''}`}>
        <ProjectHomeNav
          expanded={expandedNav}
          onToggle={(k) => setExpandedNav((prev) => (prev === k ? null : k))}
          teams={teams}
          onOpenNav={openNav}
          onOpenTeam={openTeam}
          onCollapse={() => setNavCollapsed(true)}
          onRestored={onProjectRestored}
        />

        <main className="pj-home-content scrollbar-thin">
          {navCollapsed && (
            <button
              type="button"
              className="pj-teams-side-new pj-home-navexpand"
              onClick={() => setNavCollapsed(false)}
              aria-label={tProject('project.home.nav_collapse')}
              title={tProject('project.home.nav_collapse')}
            >
              »
            </button>
          )}

          {/* ★ 2026-10-07 — 운영이 «그리는» 갈래의 통합 검색창(사용자 결정 ㉠-2).
              ⛔ 종전에는 검색창이 AW-001 홈(로컬·dev)에만 있어, 운영 사용자는 서버의
                 /project-home/search 에 닿는 길이 «0곳»이었다.
              ⚠ 격자 «위»에 한 줄로 둔다 — AW-001 홈이 쓰는 그 자리와 같은 꼴이다.
              ⚠ 「찾기」를 눌러야 간다 ⇒ 적는 동안 서버를 안 부른다(디바운스가 필요 없는 까닭). */}
          <ProjectSearchBox
            value={searchDraft}
            onChange={setSearchDraft}
            onSubmit={(q) => navigate(projectSearchTarget(q))}
          />

          <div className="pj-home-grid">
            <div className="pj-home-cell pj-home-cell--6">
              <TeamsWidget teams={teams} onOpenTeam={openTeam} onOpenAll={() => openNav('teams')} />
            </div>
            <div className="pj-home-cell pj-home-cell--6">
              {/* ⛔⛔ 2026-10-12 — 통계로 가는 길을 «걷었다».
                  사용자 결정 ⇒ 「1. /project/stats 삭제해라 ★ 화면도 필요없다.」
                  ⇒ 종전에는 이 자리가 「운영이 그리는 갈래에서 그 화면으로 가는 유일한 길」이었다
                     (2026-10-07 · 그때는 입구가 0곳이라 주소를 직접 쳐야 열렸다).
                  ⇒ ⇒ 그 방향이 뒤집혔다 — 입구를 만드는 것이 아니라 화면을 걷는다.
                  ⚠ 정본(feature/dev-syk)에는 그 화면이 «있다» — 되살리려 하지 말 것.
                     project-stats-removed.guard.test.ts 가 잠근다. */}
              {/* ⛔ 2026-10-12 — WeeklyRateWidget 을 걷었다(이 갈래는 운영에서 안 돈다). */}
            </div>
            <div className="pj-home-cell pj-home-cell--5">
              <ProjectsWidget
                projects={projects}
                onOpenProject={(p) => openProject(p.id)}
                onOpenAll={() => openNav('projects')}  /* → /task (내 프로젝트) */
              />
            </div>
            <div className="pj-home-cell pj-home-cell--7">
              <MyTaskProgressList
                items={myTasks}
                loading={myTasksLoading}
                error={myTasksError}
                onOpenProject={openProject}
              />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="pj-page">
      <header className="pj-header">
        <h1 className="pj-title">{tProject('project.title')}</h1>
      </header>

      {/* ⛔ 2026-10-12 — ProjectStatsCards 를 걷었다. 이 마지막 갈래는 두 깃발이 «둘 다 0»
          일 때만 닿아 운영에서 안 돈다 ⇒ 헤더 ＋ 내 할일 목록은 그대로 남는다. */}

      <div className="pj-mytasks-full">
        <MyTaskProgressList
          items={myTasks}
          loading={myTasksLoading}
          error={myTasksError}
          onOpenProject={openProject}
        />
      </div>
    </div>
  );
}
